import type { WidgetConfig, InitOptions, WidgetTheme, Locale, FormCollectionConfig, FormFieldDef } from "../types";
import { mediaUrl } from "./origin";
import WIDGET_FORMS from "../../../shared/widget-forms.json" with { type: "json" };

const FORM_PRESETS = (WIDGET_FORMS as { forms: Record<string, { label: string; fields: FormFieldDef[] }> }).forms;

/** 把后端返回的 formCollection 归一化，并按 formType 从内置预设补全 fields。 */
function normalizeFormCollection(raw: any): FormCollectionConfig | undefined {
  if (!raw || !raw.enabled) return undefined;
  const formType = raw.formType === "domestic" || raw.formType === "overseas" ? raw.formType : "";
  const preset = formType ? FORM_PRESETS[formType] : null;
  if (!preset) return undefined; // 启用但未选有效表单 → 视为关闭
  return {
    enabled: true,
    formType,
    triggerTurn: raw.triggerTurn === 3 ? 3 : 2,
    inviteText: typeof raw.inviteText === "string" ? raw.inviteText : "",
    fields: preset.fields,
  };
}

const DEFAULT_THEME: Record<Locale, Omit<WidgetTheme, "locale">> = {
  "zh-CN": { primaryColor: "#533afd", position: "bottom-right", title: "联系客服", welcomeText: "One Call, Global Talk · AI 实时翻译" },
  "en-US": { primaryColor: "#533afd", position: "bottom-right", title: "Contact Us", welcomeText: "One Call, Global Talk · AI Translation" },
};

function mergeTheme(remote?: Partial<WidgetTheme>, locale: Locale = "zh-CN"): WidgetTheme {
  const base = DEFAULT_THEME[locale] || DEFAULT_THEME["zh-CN"];
  return { primaryColor: remote?.primaryColor || base.primaryColor, position: remote?.position || base.position, locale: remote?.locale || locale, title: remote?.title || base.title, welcomeText: remote?.welcomeText || base.welcomeText };
}

async function fetchWidgetConfig(apiBase: string, widgetId: string): Promise<WidgetConfig> {
  const base = apiBase.replace(/\/+$/, "");
  const url = `${base}/widget/${encodeURIComponent(widgetId)}/config`;
  const res = await fetch(url, { headers: { Accept: "application/json" } });
  if (!res.ok) throw new Error(`Failed to fetch widget config: ${res.status}`);
  return (await res.json()) as WidgetConfig;
}

function defaultConfig(widgetId: string): WidgetConfig {
  return {
    widgetId,
    features: ["callback", "text_chat"],
    theme: DEFAULT_THEME["zh-CN"] as WidgetTheme,
    imChannels: [],
    voiceFallbackToCallback: true,
  };
}

export async function loadConfig(opts: InitOptions, apiBase?: string): Promise<WidgetConfig> {
  const base = apiBase || opts.apiBase || "/api";
  // 首屏可能在后端 API 未就绪/重启时拉取失败 → 重试若干次再回落默认配置，
  // 否则会只显示「文本+回拨」（默认特性），缺少语音/IM，需手动刷新才恢复。
  const maxAttempts = 4;
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      const remote = await fetchWidgetConfig(base, opts.widgetId);
      return {
        widgetId: remote.widgetId || opts.widgetId,
        features: remote.features || ["callback"],
        theme: mergeTheme(remote.theme, remote.theme?.locale || "zh-CN"),
        imChannels: remote.imChannels || [],
        voiceFallbackToCallback: remote.voiceFallbackToCallback ?? true,
        receptionistId: (remote as any).receptionistId || undefined,
        receptionistName: (remote as any).receptionistName || null,
        textChatWelcome: (remote as any).textChatWelcome || "你好，有什么可以帮你的？",
        buttonLogo: mediaUrl((remote as any).buttonLogo),
        aiAvatar: mediaUrl((remote as any).aiAvatar),
        quickReplies: (remote as any).quickReplies || [],
        displayMode: (remote as any).displayMode === "expanded" ? "expanded" : "collapsed",
        expandedShowLabels: (remote as any).expandedShowLabels !== false,
        expandedDirection: (remote as any).expandedDirection === "horizontal" ? "horizontal" : "vertical",
        expandedOrder: (remote as any).expandedOrder || [],
        formCollection: normalizeFormCollection((remote as any).formCollection),
      };
    } catch (err) {
      if (attempt < maxAttempts) {
        if (opts.debug) console.warn(`[OneCallSDK] 配置获取失败(第 ${attempt}/${maxAttempts} 次)，${attempt * 400}ms 后重试`, err);
        await new Promise(function (r) { setTimeout(r, attempt * 400); });
        continue;
      }
      if (opts.debug) console.warn("[OneCallSDK] 无法获取远程配置，使用默认配置:", err);
      return defaultConfig(opts.widgetId);
    }
  }
  return defaultConfig(opts.widgetId);
}
